//src/components/gro/QuestionnaireVersionBuilder.tsx
import { useEffect, useMemo, useRef, useState } from "react";
import { Pencil, Plus, Trash2, WandSparkles } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type {
  QuestionnaireDimension,
  QuestionnaireOption,
  QuestionnaireOptionUpsertIn,
  QuestionnaireQuestion,
  QuestionnaireQuestionUpsertIn,
  QuestionnaireResponseType,
  QuestionnaireSection,
  QuestionnaireSectionUpsertIn,
  QuestionnaireVersionDetail,
} from "@/types/gro";

type BuilderMode = "questions" | "dimensions" | "sections";

type Props = {
  version: QuestionnaireVersionDetail;
  availableDimensions: QuestionnaireDimension[];
  canWrite: boolean;
  mode?: BuilderMode;

  onCreateSection: (body: QuestionnaireSectionUpsertIn) => Promise<void>;
  onUpdateSection: (
    sectionId: string,
    body: QuestionnaireSectionUpsertIn,
  ) => Promise<void>;
  onDeleteSection: (sectionId: string) => Promise<void>;

  onCreateQuestion: (
    body: QuestionnaireQuestionUpsertIn,
  ) => Promise<QuestionnaireQuestion | void>;
  onUpdateQuestion: (
    questionId: string,
    body: QuestionnaireQuestionUpsertIn,
  ) => Promise<void>;
  onDeleteQuestion: (questionId: string) => Promise<void>;

  onCreateOption: (
    questionId: string,
    body: QuestionnaireOptionUpsertIn,
  ) => Promise<void>;
  onUpdateOption: (
    optionId: string,
    questionId: string,
    body: QuestionnaireOptionUpsertIn,
  ) => Promise<void>;
  onDeleteOption: (optionId: string, questionId: string) => Promise<void>;
};

type SectionFormState = QuestionnaireSectionUpsertIn;
type QuestionFormState = QuestionnaireQuestionUpsertIn;
type OptionFormState = QuestionnaireOptionUpsertIn;
type DraftOption = QuestionnaireOptionUpsertIn & { id?: string; tempId: string };

const RESPONSE_TYPE_LABEL: Record<QuestionnaireResponseType, string> = {
  single_choice: "Escala de avaliação (Likert)",
  multiple_choice: "Múltipla escolha",
  numeric: "Numérica inteira",
  text: "Texto livre",
  system_select: "Seleção do sistema",
  boolean: "Booleana",
};

const VISIBLE_RESPONSE_TYPES: QuestionnaireResponseType[] = [
  "single_choice",
  "text",
  "numeric",
  "system_select",
  "multiple_choice",
];

const SYSTEM_SELECT_SOURCE_LABEL = {
  departments: "Departamentos",
  job_positions: "Cargos",
  units: "Unidades",
} as const;

function getSystemSelectSourceLabel(source?: string | null) {
  if (!source) return "Cadastro do sistema";
  return SYSTEM_SELECT_SOURCE_LABEL[source as keyof typeof SYSTEM_SELECT_SOURCE_LABEL] ?? source;
}

const EMPTY_SECTION: SectionFormState = {
  section_order: 1,
  title: "",
  description: "",
};

const EMPTY_QUESTION: QuestionFormState = {
  section_id: null,
  question_order: 1,
  question_code: "",
  statement: "",
  help_text: "",
  response_type: "single_choice",
  is_required: true,
  is_reversed: false,
  weight: 1,
  dimension_ids: [],
  system_select_source: null,
  min_selected_options: null,
  max_selected_options: null,
};

const EMPTY_OPTION: OptionFormState = {
  option_order: 1,
  option_label: "",
  option_value: "1",
  raw_score: null,
  normalized_score: null,
};

function nextSectionOrder(sections: QuestionnaireSection[]) {
  return (Math.max(0, ...sections.map((s) => s.section_order)) || 0) + 1;
}

function nextQuestionOrder(questions: QuestionnaireQuestion[]) {
  return (Math.max(0, ...questions.map((q) => q.question_order)) || 0) + 1;
}

function nextQuestionCode(questions: QuestionnaireQuestion[]) {
  const maxNumber = Math.max(
    0,
    ...questions.map((q) => {
      const match = (q.question_code || "").match(/^Q(\d+)$/i);
      return match ? Number(match[1]) : 0;
    }),
  );
  return `Q${String(maxNumber + 1).padStart(4, "0")}`;
}

function nextOptionOrder(options: Array<{ option_order: number }>) {
  return (Math.max(0, ...options.map((o) => o.option_order)) || 0) + 1;
}

function normalizeQuestionCode(value: string) {
  return value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 12);
}

function isLikertQuestion(responseType: QuestionnaireResponseType) {
  return responseType === "single_choice";
}

function supportsOptions(responseType: QuestionnaireResponseType) {
  return responseType === "single_choice" || responseType === "multiple_choice";
}

function buildIntensityTemplate(): QuestionnaireOptionUpsertIn[] {
  return [
    { option_order: 1, option_label: "Muito baixa", option_value: "1", raw_score: null, normalized_score: null },
    { option_order: 2, option_label: "Baixa", option_value: "2", raw_score: null, normalized_score: null },
    { option_order: 3, option_label: "Média", option_value: "3", raw_score: null, normalized_score: null },
    { option_order: 4, option_label: "Alta", option_value: "4", raw_score: null, normalized_score: null },
    { option_order: 5, option_label: "Muito alta", option_value: "5", raw_score: null, normalized_score: null },
  ];
}

function buildAgreementTemplate(): QuestionnaireOptionUpsertIn[] {
  return [
    { option_order: 1, option_label: "Discordo totalmente", option_value: "1", raw_score: null, normalized_score: null },
    { option_order: 2, option_label: "Discordo", option_value: "2", raw_score: null, normalized_score: null },
    { option_order: 3, option_label: "Neutro", option_value: "3", raw_score: null, normalized_score: null },
    { option_order: 4, option_label: "Concordo", option_value: "4", raw_score: null, normalized_score: null },
    { option_order: 5, option_label: "Concordo totalmente", option_value: "5", raw_score: null, normalized_score: null },
  ];
}

function createDraftOption(
  option: Partial<QuestionnaireOptionUpsertIn> = {},
  id?: string,
): DraftOption {
  const optionOrder = option.option_order ?? 1;
  return {
    id,
    tempId: id ?? `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    option_order: optionOrder,
    option_label: option.option_label ?? "",
    option_value: option.option_value ?? String(optionOrder),
    raw_score: null,
    normalized_score: null,
  };
}

function toDraftOption(option: QuestionnaireOption): DraftOption {
  return createDraftOption(
    {
      option_order: option.option_order,
      option_label: option.option_label,
      option_value: option.option_value ?? String(option.option_order),
    },
    option.id,
  );
}

function appendTemplateToDrafts(
  drafts: DraftOption[],
  template: QuestionnaireOptionUpsertIn[],
): DraftOption[] {
  const startOrder = nextOptionOrder(drafts);
  return [
    ...drafts,
    ...template.map((item, index) =>
      createDraftOption({
        ...item,
        option_order: startOrder + index,
        option_value: String(startOrder + index),
      }),
    ),
  ].sort((a, b) => a.option_order - b.option_order);
}

function getDimensionLabel(
  dimensionId: string,
  availableDimensions: QuestionnaireDimension[],
  linkedDimensions: QuestionnaireDimension[],
) {
  return (
    availableDimensions.find((dim) => dim.id === dimensionId)?.name ||
    linkedDimensions.find((dim) => dim.id === dimensionId)?.name ||
    dimensionId
  );
}

function DirectionLabel({ dimension }: { dimension: QuestionnaireDimension }) {
  return (
    <span>
      {dimension.score_direction === "lower_is_better"
        ? "Menor = melhor"
        : "Maior = melhor"}
    </span>
  );
}

export function QuestionnaireVersionBuilder({
  version,
  availableDimensions,
  canWrite,
  mode = "questions",
  onCreateSection,
  onUpdateSection,
  onDeleteSection,
  onCreateQuestion,
  onUpdateQuestion,
  onDeleteQuestion,
  onCreateOption,
  onUpdateOption,
  onDeleteOption,
}: Props) {
  const isDraft = version.status === "draft" && canWrite;
  const questionFormRef = useRef<HTMLDivElement | null>(null);
  const [focusTarget, setFocusTarget] = useState<"question" | null>(null);

  const [sectionForm, setSectionForm] = useState<SectionFormState>({
    ...EMPTY_SECTION,
    section_order: nextSectionOrder(version.sections),
  });
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);

  const [questionForm, setQuestionForm] = useState<QuestionFormState>({
    ...EMPTY_QUESTION,
    section_id: version.sections[0]?.id ?? null,
    question_order: nextQuestionOrder(version.questions),
    question_code: nextQuestionCode(version.questions),
  });
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);
  const [questionCodeTouched, setQuestionCodeTouched] = useState(false);

  const [questionOptionDrafts, setQuestionOptionDrafts] = useState<DraftOption[]>([]);
  const [optionForm, setOptionForm] = useState<OptionFormState>(EMPTY_OPTION);
  const [editingDraftOptionId, setEditingDraftOptionId] = useState<string | null>(null);

  useEffect(() => {
    if (!focusTarget) return;
    const timer = window.setTimeout(() => setFocusTarget(null), 2200);
    return () => window.clearTimeout(timer);
  }, [focusTarget]);

  useEffect(() => {
    if (!editingQuestionId && !questionCodeTouched) {
      setQuestionForm((prev) => ({
        ...prev,
        question_code: nextQuestionCode(version.questions),
      }));
    }
  }, [version.questions, editingQuestionId, questionCodeTouched]);

  const sortedSections = useMemo(
    () =>
      [...version.sections].sort(
        (a, b) =>
          a.section_order - b.section_order ||
          a.title.localeCompare(b.title, "pt-BR", { sensitivity: "base" }),
      ),
    [version.sections],
  );

  const sortedQuestions = useMemo(
    () => [...version.questions].sort((a, b) => a.question_order - b.question_order),
    [version.questions],
  );

  const dimensionChoices = useMemo(
    () => [...availableDimensions].sort((a, b) => a.name.localeCompare(b.name, "pt-BR")),
    [availableDimensions],
  );

  function scrollToForm(ref: React.RefObject<HTMLDivElement | null>, target: "question") {
    setFocusTarget(target);
    window.requestAnimationFrame(() => {
      ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  function resetSectionForm() {
    setEditingSectionId(null);
    setSectionForm({
      ...EMPTY_SECTION,
      section_order: nextSectionOrder(version.sections),
    });
  }

  function resetQuestionOptionEditor(drafts: DraftOption[] = questionOptionDrafts) {
    setEditingDraftOptionId(null);
    setOptionForm({
      ...EMPTY_OPTION,
      option_order: nextOptionOrder(drafts),
      option_value: String(nextOptionOrder(drafts)),
    });
  }

  function resetQuestionForm() {
    setEditingQuestionId(null);
    setQuestionCodeTouched(false);
    setQuestionForm({
      ...EMPTY_QUESTION,
      section_id: version.sections[0]?.id ?? null,
      question_order: nextQuestionOrder(version.questions),
      question_code: nextQuestionCode(version.questions),
    });
    setQuestionOptionDrafts([]);
    resetQuestionOptionEditor([]);
  }

  async function handleSaveSection() {
    if (!sectionForm.title.trim()) {
      toast.error("Informe o título da seção.");
      return;
    }

    const body: QuestionnaireSectionUpsertIn = {
      section_order: Number(sectionForm.section_order || 1),
      title: sectionForm.title.trim(),
      description: sectionForm.description?.trim() || null,
    };

    try {
      if (editingSectionId) {
        await onUpdateSection(editingSectionId, body);
        toast.success("Seção atualizada com sucesso.");
      } else {
        await onCreateSection(body);
        toast.success("Seção criada com sucesso.");
      }
      resetSectionForm();
    } catch (e: any) {
      toast.error(e?.message || "Erro ao salvar seção.");
    }
  }

  async function handleSaveQuestion() {
    if (!questionForm.statement.trim()) {
      toast.error("Informe o enunciado da pergunta.");
      return;
    }

    const responseType = questionForm.response_type;
    const isLikert = isLikertQuestion(responseType);
    const isMultipleChoice = responseType === "multiple_choice";
    const isSystemSelect = responseType === "system_select";

    if (isLikert && questionForm.dimension_ids.length === 0) {
      toast.error("Selecione ao menos uma dimensão global para perguntas Likert.");
      return;
    }

    if (isSystemSelect && !questionForm.system_select_source) {
      toast.error("Informe a origem da Seleção do sistema.");
      return;
    }

    if (isMultipleChoice) {
      const minSelected = questionForm.min_selected_options ?? null;
      const maxSelected = questionForm.max_selected_options ?? null;

      if (questionOptionDrafts.length < 2) {
        toast.error("Cadastre pelo menos duas respostas possíveis para múltipla escolha.");
        return;
      }

      if (minSelected !== null && maxSelected !== null && minSelected > maxSelected) {
        toast.error("O mínimo de respostas não pode ser maior que o máximo.");
        return;
      }

      if (minSelected !== null && minSelected > questionOptionDrafts.length) {
        toast.error("O mínimo de respostas não pode ser maior que a quantidade de opções.");
        return;
      }
    }

    const body: QuestionnaireQuestionUpsertIn = {
      ...questionForm,
      question_order: Number(questionForm.question_order || 1),
      question_code: questionForm.question_code?.trim() || null,
      statement: questionForm.statement.trim(),
      help_text: questionForm.help_text?.trim() || null,
      is_required:
        isMultipleChoice && Number(questionForm.min_selected_options || 0) > 0
          ? true
          : questionForm.is_required,
      is_reversed: isLikert ? questionForm.is_reversed : false,
      weight: isLikert ? Number(questionForm.weight || 1) : 1,
      dimension_ids: isLikert ? questionForm.dimension_ids : [],
      system_select_source: isSystemSelect ? questionForm.system_select_source ?? null : null,
      min_selected_options: isMultipleChoice ? questionForm.min_selected_options ?? null : null,
      max_selected_options: isMultipleChoice ? questionForm.max_selected_options ?? null : null,
    };

    const desiredDrafts = supportsOptions(questionForm.response_type)
      ? [...questionOptionDrafts].sort((a, b) => a.option_order - b.option_order)
      : [];

    try {
      if (editingQuestionId) {
        await onUpdateQuestion(editingQuestionId, body);

        const existingQuestion = version.questions.find((q) => q.id === editingQuestionId);
        const existingOptions = existingQuestion?.options ?? [];

        for (const existing of existingOptions) {
          const stillExists = desiredDrafts.some((draft) => draft.id === existing.id);
          if (!stillExists) {
            await onDeleteOption(existing.id, editingQuestionId);
          }
        }

        for (const draft of desiredDrafts) {
          const payload: QuestionnaireOptionUpsertIn = {
            option_order: Number(draft.option_order || 1),
            option_label: draft.option_label.trim(),
            option_value: isLikert ? String(draft.option_value ?? draft.option_order ?? 1) : null,
            raw_score: null,
            normalized_score: null,
          };

          if (draft.id) {
            await onUpdateOption(draft.id, editingQuestionId, payload);
          } else {
            await onCreateOption(editingQuestionId, payload);
          }
        }

        toast.success("Pergunta atualizada com sucesso.");
      } else {
        const created = await onCreateQuestion(body);
        const createdQuestionId = created?.id;

        if (createdQuestionId) {
          for (const draft of desiredDrafts) {
            await onCreateOption(createdQuestionId, {
              option_order: Number(draft.option_order || 1),
              option_label: draft.option_label.trim(),
              option_value: isLikert ? String(draft.option_value ?? draft.option_order ?? 1) : null,
              raw_score: null,
              normalized_score: null,
            });
          }
        }

        toast.success("Pergunta criada com sucesso.");
      }

      resetQuestionForm();
    } catch (e: any) {
      toast.error(e?.message || "Erro ao salvar pergunta.");
    }
  }

  function handleSaveDraftOption() {
    if (!optionForm.option_label.trim()) {
      toast.error("Informe o rótulo da resposta.");
      return;
    }

    const draft: DraftOption = createDraftOption(
      {
        option_order: Number(optionForm.option_order || 1),
        option_label: optionForm.option_label.trim(),
        option_value: String(optionForm.option_value ?? optionForm.option_order ?? 1),
        raw_score: null,
        normalized_score: null,
      },
      editingDraftOptionId?.startsWith("tmp-") ? undefined : editingDraftOptionId || undefined,
    );

    if (editingDraftOptionId) {
      draft.tempId = editingDraftOptionId;
      setQuestionOptionDrafts((prev) =>
        prev
          .map((item) => (item.tempId === editingDraftOptionId ? draft : item))
          .sort((a, b) => a.option_order - b.option_order),
      );
      toast.success("Resposta atualizada no formulário.");
    } else {
      setQuestionOptionDrafts((prev) =>
        [...prev, draft].sort((a, b) => a.option_order - b.option_order),
      );
      toast.success("Resposta adicionada no formulário.");
    }

    const nextDrafts = editingDraftOptionId
      ? questionOptionDrafts.map((item) => (item.tempId === editingDraftOptionId ? draft : item))
      : [...questionOptionDrafts, draft];

    resetQuestionOptionEditor(nextDrafts);
  }

  function startEditDraftOption(option: DraftOption) {
    setEditingDraftOptionId(option.tempId);
    setOptionForm({
      option_order: option.option_order,
      option_label: option.option_label,
      option_value: option.option_value ?? String(option.option_order),
      raw_score: null,
      normalized_score: null,
    });
  }

  function deleteDraftOption(tempId: string) {
    const nextDrafts = questionOptionDrafts.filter((item) => item.tempId !== tempId);
    setQuestionOptionDrafts(nextDrafts);
    if (editingDraftOptionId === tempId) {
      resetQuestionOptionEditor(nextDrafts);
    }
  }

  function applyDraftTemplate(template: QuestionnaireOptionUpsertIn[]) {
    const nextDrafts = appendTemplateToDrafts(questionOptionDrafts, template);
    setQuestionOptionDrafts(nextDrafts);
    resetQuestionOptionEditor(nextDrafts);
    toast.success("Template de respostas aplicado no formulário.");
  }

  function startEditSection(item: QuestionnaireSection) {
    setEditingSectionId(item.id);
    setSectionForm({
      section_order: item.section_order,
      title: item.title,
      description: item.description ?? "",
    });
  }

  function startEditQuestion(item: QuestionnaireQuestion) {
    const drafts = [...item.options]
      .sort((a, b) => a.option_order - b.option_order)
      .map((option) => toDraftOption(option));

    setEditingQuestionId(item.id);
    setQuestionForm({
      section_id: item.section_id ?? null,
      question_order: item.question_order,
      question_code: item.question_code ?? "",
      statement: item.statement,
      help_text: item.help_text ?? "",
      response_type: item.response_type,
      is_required: item.is_required,
      is_reversed: item.is_reversed,
      weight: item.weight,
      dimension_ids: item.dimension_ids ?? [],
      system_select_source: item.system_select_source ?? null,
      min_selected_options: item.min_selected_options ?? null,
      max_selected_options: item.max_selected_options ?? null,
    });
    setQuestionOptionDrafts(drafts);
    setQuestionCodeTouched(true);
    resetQuestionOptionEditor(drafts);
    scrollToForm(questionFormRef, "question");
  }

  if (mode === "dimensions") {
    return (
      <div className="space-y-6">
        <Card className="p-4">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="font-semibold">Dimensões vinculadas</h3>
              <p className="text-sm text-muted-foreground">
                As dimensões são globais do tenant. Nesta versão, exibimos apenas as dimensões já vinculadas às perguntas.
              </p>
            </div>
            <Badge variant={isDraft ? "secondary" : "outline"}>
              {version.dimensions.length} vinculada(s)
            </Badge>
          </div>

          {version.dimensions.length === 0 ? (
            <div className="rounded-md border border-dashed p-6 text-sm text-muted-foreground">
              Nenhuma dimensão foi vinculada ainda. Edite uma pergunta na aba “Perguntas” e selecione uma ou mais dimensões globais.
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {version.dimensions.map((item) => (
                <Card key={item.id} className="p-3">
                  <div className="font-medium">
                    {item.name} <span className="text-muted-foreground">({item.code})</span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Direção: <DirectionLabel dimension={item} />
                  </div>
                  {item.description ? (
                    <div className="mt-1 text-sm text-muted-foreground">{item.description}</div>
                  ) : null}
                  {!item.is_active ? (
                    <Badge variant="outline" className="mt-2">Inativa</Badge>
                  ) : null}
                </Card>
              ))}
            </div>
          )}
        </Card>
      </div>
    );
  }

  if (mode === "sections") {
    return (
      <div className="space-y-6">
        <Card className="p-4">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-semibold">Seções</h3>
            <Badge variant={isDraft ? "secondary" : "outline"}>
              {isDraft ? "Editável" : "Somente leitura"}
            </Badge>
          </div>

          <div className="grid gap-4 md:grid-cols-12">
            <div className="md:col-span-2">
              <Label>Ordem *</Label>
              <Input
                className="mt-1"
                type="number"
                min={1}
                value={sectionForm.section_order}
                onChange={(e) =>
                  setSectionForm((prev) => ({
                    ...prev,
                    section_order: Number(e.target.value || 1),
                  }))
                }
                disabled={!isDraft}
              />
            </div>

            <div className="md:col-span-10">
              <Label>Título *</Label>
              <Input
                className="mt-1"
                value={sectionForm.title}
                onChange={(e) =>
                  setSectionForm((prev) => ({ ...prev, title: e.target.value }))
                }
                disabled={!isDraft}
              />
            </div>

            <div className="md:col-span-12">
              <Label>Descrição</Label>
              <textarea
                className="mt-1 min-h-[90px] w-full rounded-md border bg-background px-3 py-2 text-sm"
                value={sectionForm.description ?? ""}
                onChange={(e) =>
                  setSectionForm((prev) => ({ ...prev, description: e.target.value }))
                }
                disabled={!isDraft}
              />
            </div>
          </div>

          {isDraft ? (
            <div className="mt-4 flex gap-2">
              <Button onClick={handleSaveSection}>
                <Plus className="mr-2 h-4 w-4" />
                {editingSectionId ? "Atualizar seção" : "Adicionar seção"}
              </Button>
              {editingSectionId ? (
                <Button variant="outline" onClick={resetSectionForm}>
                  Cancelar edição
                </Button>
              ) : null}
            </div>
          ) : null}
        </Card>

        <div className="grid gap-3">
          {sortedSections.length === 0 ? (
            <Card className="p-6 text-sm text-muted-foreground">Nenhuma seção cadastrada.</Card>
          ) : (
            sortedSections.map((section) => (
              <Card key={section.id} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium">
                      {section.section_order}. {section.title}
                    </div>
                    {section.description ? (
                      <div className="mt-1 text-sm text-muted-foreground">{section.description}</div>
                    ) : null}
                  </div>

                  {isDraft ? (
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => startEditSection(section)}>
                        <Pencil className="mr-2 h-4 w-4" />Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={async () => {
                          try {
                            await onDeleteSection(section.id);
                            toast.success("Seção excluída com sucesso.");
                            if (editingSectionId === section.id) resetSectionForm();
                          } catch (e: any) {
                            toast.error(e?.message || "Erro ao excluir seção.");
                          }
                        }}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />Excluir
                      </Button>
                    </div>
                  ) : null}
                </div>
              </Card>
            ))
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div ref={questionFormRef}>
        <Card className={`p-4 transition ${focusTarget === "question" ? "ring-2 ring-primary" : ""}`}>
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-semibold">Perguntas</h3>
            <Badge variant={isDraft ? "secondary" : "outline"}>
              {isDraft ? "Editável" : "Somente leitura"}
            </Badge>
          </div>

          <div className="grid gap-4 md:grid-cols-12">
            <div className="md:col-span-2">
              <Label>Ordem *</Label>
              <Input
                className="mt-1"
                type="number"
                min={1}
                value={questionForm.question_order}
                onChange={(e) =>
                  setQuestionForm((prev) => ({
                    ...prev,
                    question_order: Number(e.target.value || 1),
                  }))
                }
                disabled={!isDraft}
              />
            </div>

            <div className="md:col-span-3">
              <Label>Código</Label>
              <Input
                className="mt-1"
                value={questionForm.question_code ?? ""}
                onChange={(e) => {
                  setQuestionCodeTouched(true);
                  setQuestionForm((prev) => ({
                    ...prev,
                    question_code: normalizeQuestionCode(e.target.value),
                  }));
                }}
                disabled={!isDraft}
              />
            </div>

            <div className="md:col-span-3">
              <Label>Seção</Label>
              <Select
                value={questionForm.section_id ?? "__none__"}
                onValueChange={(value) =>
                  setQuestionForm((prev) => ({
                    ...prev,
                    section_id: value === "__none__" ? null : value,
                  }))
                }
                disabled={!isDraft}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Sem seção" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sem seção</SelectItem>
                  {sortedSections.map((section) => (
                    <SelectItem key={section.id} value={section.id}>
                      {section.section_order}. {section.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="md:col-span-2">
              <Label>Tipo de resposta</Label>
              <Select
                value={questionForm.response_type}
                onValueChange={(value) => {
                  const responseType = value as QuestionnaireResponseType;
                  setQuestionForm((prev) => ({
                    ...prev,
                    response_type: responseType,
                    is_reversed: isLikertQuestion(responseType) ? prev.is_reversed : false,
                    weight: isLikertQuestion(responseType) ? prev.weight : 1,
                    dimension_ids: isLikertQuestion(responseType) ? prev.dimension_ids : [],
                    system_select_source: responseType === "system_select" ? prev.system_select_source : null,
                    min_selected_options: responseType === "multiple_choice" ? prev.min_selected_options : null,
                    max_selected_options: responseType === "multiple_choice" ? prev.max_selected_options : null,
                  }));
                  if (!supportsOptions(responseType)) {
                    setQuestionOptionDrafts([]);
                    resetQuestionOptionEditor([]);
                  }
                }}
                disabled={!isDraft}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {VISIBLE_RESPONSE_TYPES.map((value) => (
                    <SelectItem key={value} value={value}>
                      {RESPONSE_TYPE_LABEL[value]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {isLikertQuestion(questionForm.response_type) ? (
              <div className="md:col-span-2">
              <Label>Peso</Label>
              <Input
                className="mt-1"
                type="number"
                min={0}
                step="0.1"
                value={questionForm.weight}
                onChange={(e) =>
                  setQuestionForm((prev) => ({
                    ...prev,
                    weight: Number(e.target.value || 1),
                  }))
                }
                disabled={!isDraft}
              />
            </div>
            ) : null}

            <div className="md:col-span-12">
              <Label>Enunciado *</Label>
              <textarea
                className="mt-1 min-h-[100px] w-full rounded-md border bg-background px-3 py-2 text-sm"
                value={questionForm.statement}
                onChange={(e) =>
                  setQuestionForm((prev) => ({ ...prev, statement: e.target.value }))
                }
                disabled={!isDraft}
              />
            </div>

            <div className="md:col-span-12">
              <Label>Texto de apoio</Label>
              <textarea
                className="mt-1 min-h-[80px] w-full rounded-md border bg-background px-3 py-2 text-sm"
                value={questionForm.help_text ?? ""}
                onChange={(e) =>
                  setQuestionForm((prev) => ({ ...prev, help_text: e.target.value }))
                }
                disabled={!isDraft}
              />
            </div>

            {questionForm.response_type === "system_select" ? (
              <div className="md:col-span-6">
                <Label>Origem das opções *</Label>
                <div className="mt-2 rounded-md border p-3">
                  <Select
                    value={questionForm.system_select_source ?? ""}
                    onValueChange={(value) =>
                      setQuestionForm((prev) => ({
                        ...prev,
                        system_select_source: value as any,
                      }))
                    }
                    disabled={!isDraft}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a origem" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(SYSTEM_SELECT_SOURCE_LABEL).map(([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="mt-2 text-xs text-muted-foreground">
                    As opções serão carregadas automaticamente do cadastro selecionado.
                  </p>
                </div>
              </div>
            ) : null}

            {questionForm.response_type === "multiple_choice" ? (
              <div className="md:col-span-6">
                <Label>Limites de seleção</Label>
                <div className="mt-2 rounded-md border p-3">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div>
                      <Label className="text-xs text-muted-foreground">Mínimo de respostas</Label>
                      <Input
                        className="mt-1"
                        type="number"
                        min={0}
                        value={questionForm.min_selected_options ?? ""}
                        onChange={(e) =>
                          setQuestionForm((prev) => ({
                            ...prev,
                            min_selected_options: e.target.value === "" ? null : Number(e.target.value),
                            is_required: Number(e.target.value || 0) > 0 ? true : prev.is_required,
                          }))
                        }
                        disabled={!isDraft}
                      />
                    </div>

                    <div>
                      <Label className="text-xs text-muted-foreground">Máximo de respostas</Label>
                      <Input
                        className="mt-1"
                        type="number"
                        min={1}
                        value={questionForm.max_selected_options ?? ""}
                        onChange={(e) =>
                          setQuestionForm((prev) => ({
                            ...prev,
                            max_selected_options: e.target.value === "" ? null : Number(e.target.value),
                          }))
                        }
                        disabled={!isDraft}
                      />
                    </div>
                  </div>

                  <p className="mt-2 text-xs text-muted-foreground">
                    Se o mínimo for maior que zero, a pergunta passa a ser obrigatória automaticamente.
                    Se o máximo ficar vazio, não haverá limite máximo de seleção.
                  </p>
                </div>
              </div>
            ) : null}

            {isLikertQuestion(questionForm.response_type) ? (
              <div className="md:col-span-6">
              <Label>Dimensões globais</Label>
              <div className="mt-2 grid gap-2 rounded-md border p-3 md:grid-cols-2">
                {dimensionChoices.length === 0 ? (
                  <div className="text-sm text-muted-foreground">
                    Nenhuma dimensão global cadastrada no sistema.
                  </div>
                ) : (
                  dimensionChoices.map((dimension) => {
                    const checked = questionForm.dimension_ids.includes(dimension.id);
                    return (
                      <label
                        key={dimension.id}
                        className="flex items-start gap-3 rounded-md border p-2 text-sm"
                      >
                        <Checkbox
                          checked={checked}
                          disabled={!isDraft}
                          onCheckedChange={() =>
                            setQuestionForm((prev) => ({
                              ...prev,
                              dimension_ids: checked
                                ? prev.dimension_ids.filter((id) => id !== dimension.id)
                                : [...prev.dimension_ids, dimension.id],
                            }))
                          }
                        />
                        <span>
                          <strong>{dimension.name}</strong> ({dimension.code})
                          <span className="block text-xs text-muted-foreground">
                            <DirectionLabel dimension={dimension} />
                            {!dimension.is_active ? " • Inativa" : ""}
                          </span>
                        </span>
                      </label>
                    );
                  })
                )}
              </div>
            </div>
            ) : null}

            <div className="md:col-span-6">
              <Label>Comportamento</Label>
              <div className="mt-2 grid gap-2 rounded-md border p-3">
                <label className="flex items-center gap-3 text-sm">
                  <Checkbox
                    checked={questionForm.is_required}
                    disabled={!isDraft}
                    onCheckedChange={(checked) =>
                      setQuestionForm((prev) => ({
                        ...prev,
                        is_required: checked === true,
                      }))
                    }
                  />
                  Resposta obrigatória
                </label>
                {isLikertQuestion(questionForm.response_type) ? (
                  <label className="flex items-center gap-3 text-sm">
                    <Checkbox
                      checked={questionForm.is_reversed}
                      disabled={!isDraft}
                      onCheckedChange={(checked) =>
                        setQuestionForm((prev) => ({
                          ...prev,
                          is_reversed: checked === true,
                        }))
                      }
                    />
                    Escala invertida
                  </label>
                ) : null}
              </div>
            </div>
          </div>

          {supportsOptions(questionForm.response_type) ? (
            <div className="mt-6 space-y-4 rounded-md border p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h4 className="font-medium">Respostas possíveis</h4>
                  <p className="text-sm text-muted-foreground">
                    Monte as opções antes de salvar a pergunta.
                  </p>
                </div>

                {isDraft && isLikertQuestion(questionForm.response_type) ? (
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={() => applyDraftTemplate(buildIntensityTemplate())}>
                      <WandSparkles className="mr-2 h-4 w-4" />Escala de intensidade
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => applyDraftTemplate(buildAgreementTemplate())}>
                      <WandSparkles className="mr-2 h-4 w-4" />Escala de concordância
                    </Button>
                  </div>
                ) : null}
              </div>

              <div className="grid gap-4 md:grid-cols-12">
                <div className="md:col-span-2">
                  <Label>Ordem</Label>
                  <Input
                    className="mt-1"
                    type="number"
                    min={1}
                    value={optionForm.option_order}
                    onChange={(e) =>
                      setOptionForm((prev) => ({
                        ...prev,
                        option_order: Number(e.target.value || 1),
                        option_value: String(e.target.value || 1),
                      }))
                    }
                    disabled={!isDraft}
                  />
                </div>
                <div className="md:col-span-6">
                  <Label>Rótulo</Label>
                  <Input
                    className="mt-1"
                    value={optionForm.option_label}
                    onChange={(e) =>
                      setOptionForm((prev) => ({ ...prev, option_label: e.target.value }))
                    }
                    disabled={!isDraft}
                  />
                </div>
                {isLikertQuestion(questionForm.response_type) ? (
                  <div className="md:col-span-2">
                  <Label>Valor</Label>
                  <Input
                    className="mt-1"
                    value={optionForm.option_value ?? ""}
                    onChange={(e) =>
                      setOptionForm((prev) => ({ ...prev, option_value: e.target.value }))
                    }
                    disabled={!isDraft}
                  />
                </div>
                ) : null}
                <div className="md:col-span-2 flex items-end gap-2">
                  <Button className="w-full" onClick={handleSaveDraftOption} disabled={!isDraft}>
                    {editingDraftOptionId ? "Atualizar" : "Adicionar"}
                  </Button>
                </div>
              </div>

              <div className="grid gap-2">
                {questionOptionDrafts.length === 0 ? (
                  <div className="text-sm text-muted-foreground">Nenhuma resposta adicionada.</div>
                ) : (
                  questionOptionDrafts
                    .sort((a, b) => a.option_order - b.option_order)
                    .map((option) => (
                      <div key={option.tempId} className="flex items-center justify-between rounded-md border p-3 text-sm">
                        <div>
                          <strong>{option.option_order}. {option.option_label}</strong>
                          {isLikertQuestion(questionForm.response_type) ? (
                            <div className="text-muted-foreground">Valor: {option.option_value ?? "-"}</div>
                          ) : null}
                        </div>
                        {isDraft ? (
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => startEditDraftOption(option)}>
                              <Pencil className="mr-2 h-4 w-4" />Editar
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => deleteDraftOption(option.tempId)}>
                              <Trash2 className="mr-2 h-4 w-4" />Excluir
                            </Button>
                          </div>
                        ) : null}
                      </div>
                    ))
                )}
              </div>
            </div>
          ) : null}

          {isDraft ? (
            <div className="mt-4 flex gap-2">
              <Button onClick={handleSaveQuestion}>
                <Plus className="mr-2 h-4 w-4" />
                {editingQuestionId ? "Atualizar pergunta" : "Adicionar pergunta"}
              </Button>
              {editingQuestionId ? (
                <Button variant="outline" onClick={resetQuestionForm}>
                  Cancelar edição
                </Button>
              ) : null}
            </div>
          ) : null}
        </Card>
      </div>

      <div className="grid gap-3">
        {sortedQuestions.length === 0 ? (
          <Card className="p-6 text-sm text-muted-foreground">Nenhuma pergunta cadastrada.</Card>
        ) : (
          sortedQuestions.map((question) => (
            <Card key={question.id} className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-2">
                  <div className="font-medium">
                    {question.question_order}. {question.statement}
                  </div>

                  <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                    {question.question_code ? <Badge variant="outline">{question.question_code}</Badge> : null}
                    <Badge variant="outline">{RESPONSE_TYPE_LABEL[question.response_type]}</Badge>
                    {question.section_id ? (
                      <Badge variant="outline">
                        {sortedSections.find((section) => section.id === question.section_id)?.title || "Seção"}
                      </Badge>
                    ) : null}
                    {isLikertQuestion(question.response_type) ? (
                      <Badge variant="outline">Peso {question.weight}</Badge>
                    ) : null}
                    {question.is_required ? <Badge variant="secondary">Obrigatória</Badge> : null}
                    {isLikertQuestion(question.response_type) && question.is_reversed ? (
                      <Badge variant="secondary">Invertida</Badge>
                    ) : null}
                    {question.response_type === "system_select" && question.system_select_source ? (
                      <Badge variant="outline">
                        {getSystemSelectSourceLabel(question.system_select_source)}
                      </Badge>
                    ) : null}
                    {question.response_type === "multiple_choice" && question.min_selected_options ? (
                      <Badge variant="outline">Mín. {question.min_selected_options}</Badge>
                    ) : null}
                    {question.response_type === "multiple_choice" && question.max_selected_options ? (
                      <Badge variant="outline">Máx. {question.max_selected_options}</Badge>
                    ) : null}
                  </div>

                  {question.help_text ? (
                    <div className="text-sm text-muted-foreground">{question.help_text}</div>
                  ) : null}

                  {isLikertQuestion(question.response_type) ? (
                    <div className="text-sm text-muted-foreground">
                      Dimensões: {question.dimension_ids.length > 0
                        ? question.dimension_ids
                            .map((id) => getDimensionLabel(id, availableDimensions, version.dimensions))
                            .join(", ")
                        : "Nenhuma"}
                    </div>
                  ) : null}

                  {question.options.length > 0 ? (
                    <div className="text-sm text-muted-foreground">
                      Respostas: {question.options
                        .sort((a, b) => a.option_order - b.option_order)
                        .map((option) => `${option.option_order}. ${option.option_label}`)
                        .join(" • ")}
                    </div>
                  ) : null}
                </div>

                {isDraft ? (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => startEditQuestion(question)}>
                      <Pencil className="mr-2 h-4 w-4" />Editar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        try {
                          await onDeleteQuestion(question.id);
                          toast.success("Pergunta excluída com sucesso.");
                          if (editingQuestionId === question.id) resetQuestionForm();
                        } catch (e: any) {
                          toast.error(e?.message || "Erro ao excluir pergunta.");
                        }
                      }}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />Excluir
                    </Button>
                  </div>
                ) : null}
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
